<template>
    <ModalBase
        :name="'cambiar-imagen-' + evento.id"
        :title="'Editar imagen entrada'"
        :subtitle="`Evento: ${evento.nombre_completo || evento.name || ''}`"
        :width="700"
        @close="close"
    >
        <div class="tw:space-y-6">
            <!-- Section 1: Email header -->
            <div>
                <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1">
                    {{ 'Imagen cabecera email' }}:
                </label>
                <p class="tw:text-xs tw:text-gray-500 tw:mb-2">
                    {{ 'MEDIDAS: 2182x287 - FORMATO: JPG - PESO MÁXIMO: Menos de 250 KB' }}
                    <br />
                    {{ '* No introduzcas texto en la imagen.' }}
                </p>
                <template v-if="getExistingUrl('email_header') && !isDeleted('email_header')">
                    <img
                        :src="getExistingUrl('email_header')"
                        class="tw:max-h-48 tw:rounded tw:mb-2"
                        :alt="'Imagen cabecera email'"
                    />
                    <div>
                        <Button variant="danger" @click="deleteImage('email_header')">{{ 'Borrar' }}</Button>
                    </div>
                </template>
                <FileDropzone
                    v-else-if="uploadUrl"
                    :key="'email_header-' + imageFilenames.email_header"
                    :url="uploadUrl"
                    :height="287"
                    :width="2182"
                    @success="(file, response) => onImageUploaded('email_header', file, response)"
                    @removed="() => onImageRemoved('email_header')"
                />
            </div>

            <!-- Section 2: Publicity -->
            <div>
                <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1">
                    {{ 'Imagen publicidad email' }}:
                </label>
                <p class="tw:text-xs tw:text-gray-500 tw:mb-2">
                    {{ 'MEDIDAS: 2182x667 - FORMATO: JPG - PESO MÁXIMO: Menos de 250 KB' }}
                    <br />
                    {{ '* No introduzcas texto en la imagen.' }}
                </p>
                <template v-if="getExistingUrl('publicity') && !isDeleted('publicity')">
                    <img
                        :src="getExistingUrl('publicity')"
                        class="tw:max-h-48 tw:rounded tw:mb-2"
                        :alt="'Imagen publicidad email'"
                    />
                    <div>
                        <Button variant="danger" @click="deleteImage('publicity')">{{ 'Borrar' }}</Button>
                    </div>
                </template>
                <FileDropzone
                    v-else-if="uploadUrl"
                    :key="'publicity-' + imageFilenames.publicity"
                    :url="uploadUrl"
                    :height="667"
                    :width="2182"
                    @success="(file, response) => onImageUploaded('publicity', file, response)"
                    @removed="() => onImageRemoved('publicity')"
                />
            </div>

            <!-- Section 3: Sponsor -->
            <div>
                <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1">
                    {{ 'Imagen patrocinadores email' }}:
                </label>
                <p class="tw:text-xs tw:text-gray-500 tw:mb-2">
                    {{ 'MEDIDAS: 2182x319 - FORMATO: JPG - PESO MÁXIMO: Menos de 250 KB' }}
                    <br />
                    {{ '* No introduzcas texto en la imagen.' }}
                </p>
                <template v-if="getExistingUrl('sponsor') && !isDeleted('sponsor')">
                    <img
                        :src="getExistingUrl('sponsor')"
                        class="tw:max-h-48 tw:rounded tw:mb-2"
                        :alt="'Imagen patrocinadores email'"
                    />
                    <div>
                        <Button variant="danger" @click="deleteImage('sponsor')">{{ 'Borrar' }}</Button>
                    </div>
                </template>
                <FileDropzone
                    v-else-if="uploadUrl"
                    :key="'sponsor-' + imageFilenames.sponsor"
                    :url="uploadUrl"
                    :height="319"
                    :width="2182"
                    @success="(file, response) => onImageUploaded('sponsor', file, response)"
                    @removed="() => onImageRemoved('sponsor')"
                />
            </div>
        </div>

        <template #footer>
            <div class="tw:flex tw:justify-between tw:gap-3 tw:w-full">
                <Button
                    variant="primary"
                    :disabled="loading"
                    @click="saveImages"
                >
                    {{ 'Guardar' }}
                </Button>
                <Button
                    variant="ghost"
                    :disabled="loading"
                    @click="close"
                >
                    {{ 'Cerrar' }}
                </Button>
            </div>
        </template>
    </ModalBase>
</template>

<script setup>
    import { ref, computed, onMounted, getCurrentInstance } from 'vue';
    import store from '@/store';
    import ModalBase from '@/modules/admin/ui/ModalBase.vue';
    import FileDropzone from '@/modules/admin/ui/FileDropzone.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import { useToast } from '@/composables/common/useToast';

    const props = defineProps({
        evento: { type: Object, required: true }
    });

    const emit = defineEmits(['close', 'updated']);

    const instance = getCurrentInstance();
    const $modal = instance.proxy.$modal;
    const toast = useToast();

    const loading = ref(false);

    const sectionMapping = {
        email_header: { eventoField: 'imagen_cabecera', eventoUrlField: 'imagen_cabecera_url' },
        publicity: { eventoField: 'imagen_publicidad', eventoUrlField: 'imagen_publicidad_url' },
        sponsor: { eventoField: 'imagen_patrocinadores', eventoUrlField: 'imagen_patrocinadores_url' }
    };

    // Tracks current filename for each section (null = no image)
    const imageFilenames = ref({
        email_header: null,
        publicity: null,
        sponsor: null
    });

    // Tracks which sections had their existing image deleted
    const deletedSections = ref({
        email_header: false,
        publicity: false,
        sponsor: false
    });

    const uploadUrl = computed(() => {
        const base = store.getters.getConfig?.API_BASE || '';
        const token = localStorage.getItem('token') || '';
        return `${base}/upload/image?api_key=${token}&permisos=public`;
    });

    function getExistingUrl(section) {
        const mapping = sectionMapping[section];
        return props.evento[mapping.eventoUrlField] || null;
    }

    function isDeleted(section) {
        return deletedSections.value[section];
    }

    function getExistingField(section) {
        const mapping = sectionMapping[section];
        return props.evento[mapping.eventoField] || null;
    }

    function onImageUploaded(section, file, response) {
        const filename = typeof response === 'string' ? response : (response.filename || response.name || response);
        imageFilenames.value[section] = filename;
    }

    function onImageRemoved(section) {
        imageFilenames.value[section] = null;
    }

    function deleteImage(section) {
        deletedSections.value[section] = true;
        imageFilenames.value[section] = null;
    }

    function resetState() {
        imageFilenames.value = {
            email_header: null,
            publicity: null,
            sponsor: null
        };
        deletedSections.value = {
            email_header: false,
            publicity: false,
            sponsor: false
        };
    }

    function close() {
        resetState();
        emit('close');
        $modal.hide('cambiar-imagen-' + props.evento.id);
    }

    function saveImages() {
        loading.value = true;

        const params = {
            filters: {},
            evento_id: props.evento.id,
            imagen_cabecera: deletedSections.value.email_header
                ? null
                : (imageFilenames.value.email_header || getExistingField('email_header')),
            imagen_publicidad: deletedSections.value.publicity
                ? null
                : (imageFilenames.value.publicity || getExistingField('publicity')),
            imagen_patrocinadores: deletedSections.value.sponsor
                ? null
                : (imageFilenames.value.sponsor || getExistingField('sponsor'))
        };

        const alias = store.getters.getConfig?.getVuexAlias?.('back.events.editar.imagenes-entradas') || 'BACK_EVENTS_EDITAR_IMAGENES_ENTRADAS';
        store.dispatch(alias, params).then(() => {
            toast.success('Imágenes guardadas correctamente');
            emit('updated');
            close();
        }).catch(() => {
            toast.error('Error al guardar las imágenes');
        }).finally(() => {
            loading.value = false;
        });
    }
</script>